import React from 'react';
import { Avatar, Badge, Box, Button, Flex, Group, Paper, Stack, Text, Title } from '@mantine/core';
import { IconPencil, IconUsers } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { Eyebrow } from './primitives';

export interface MySummaryRow {
  userId: string;
  name: string;
  avatar?: string;
  hasDraft: boolean;
  isShared: boolean;
  isPublished: boolean;
  // Сколько форм по этому человеку уже сдано из скольких. Саммари
  // разблокируется по-субъектно, поэтому пока сдано не всё, писать нельзя —
  // сервер отвечает 409, и кнопку тут держим выключенной.
  feedbackSubmitted: number;
  feedbackTotal: number;
}

interface MySummariesCardProps {
  rows: MySummaryRow[];
  onWrite: (userId: string) => void;
}

const statusOf = (row: MySummaryRow): { key: string; color: string } => {
  if (row.isPublished) return { key: 'published', color: 'teal' };
  if (row.isShared) return { key: 'shared', color: 'blue' };
  if (row.hasDraft) return { key: 'draft', color: 'yellow' };
  return { key: 'empty', color: 'gray' };
};

/**
 * Рабочий список того, кто пишет саммари. Без него не-админ вообще не имел
 * входа в редактор: право писать у него есть, а единственная страница с
 * редактором — админская.
 */
export const MySummariesCard: React.FC<MySummariesCardProps> = ({ rows, onWrite }) => {
  const { t } = useTranslation();

  return (
    <Paper withBorder p="xl" radius="md">
      <Flex justify="space-between" align="flex-end" mb="lg" gap="md">
        <Box>
          <Eyebrow>{t('perfReview.home.mySummaries.section')}</Eyebrow>
          <Title order={3} size="h4" mt={6} style={{ letterSpacing: '-0.01em' }}>
            {t('perfReview.home.mySummaries.title')}
          </Title>
          <Text size="sm" c="dimmed" mt={4}>
            {t('perfReview.home.mySummaries.subtitle')}
          </Text>
        </Box>
        <Badge variant="light" color="grape" size="sm" leftSection={<IconUsers size={12} />}>
          {rows.length}
        </Badge>
      </Flex>

      <Stack gap="xs">
        {rows.map((row) => {
          const status = statusOf(row);
          const isLocked = !row.isPublished && row.feedbackSubmitted < row.feedbackTotal;
          return (
            <Paper
              key={row.userId}
              p="sm"
              radius="md"
              withBorder
              style={{ background: 'var(--mantine-color-default-hover)' }}
            >
              <Group gap="sm" wrap="nowrap">
                <Avatar src={row.avatar} radius="xl" size={32}>
                  {row.name.slice(0, 1)}
                </Avatar>
                <Box style={{ flex: 1, minWidth: 0 }}>
                  <Text size="sm" fw={500} truncate>
                    {row.name}
                  </Text>
                  {isLocked && (
                    <Text size="xs" c="dimmed">
                      {t('perfReview.home.mySummaries.waitingFeedback', {
                        submitted: row.feedbackSubmitted,
                        total: row.feedbackTotal,
                      })}
                    </Text>
                  )}
                </Box>
                <Badge variant="light" color={status.color} size="sm">
                  {t(`perfReview.home.mySummaries.status.${status.key}`)}
                </Badge>
                <Button
                  size="xs"
                  variant="light"
                  disabled={isLocked}
                  leftSection={<IconPencil size={14} />}
                  onClick={() => onWrite(row.userId)}
                >
                  {t(
                    row.hasDraft || row.isPublished
                      ? 'perfReview.home.mySummaries.openAction'
                      : 'perfReview.home.mySummaries.writeAction',
                  )}
                </Button>
              </Group>
            </Paper>
          );
        })}
      </Stack>
    </Paper>
  );
};
